/**
 * Calculator arithmetic for numeric cells ("45*1.2", "2000 x 1.75", "45 + 20%").
 *
 * A small recursive-descent parser over exact rationals (BigInt numerator / denominator), so no
 * floating point and no eval. The result is rounded half-up to a fixed number of decimals and
 * returned as a plain decimal string, which is what the server receives.
 *
 * Grammar:
 *   expr    := term (("+" | "-") term)*
 *   term    := unary (("*" | "x" | "×" | "/" | "÷") unary)*
 *   unary   := ("-" | "+") unary | postfix
 *   postfix := primary "%"?
 *   primary := number | "(" expr ")"
 *
 * Percent semantics (a calculator's): on the right of + or −, "a + b%" means a increased by b
 * percent (a × (1 + b/100)) and "a − b%" a decreased by b percent. Anywhere else "b%" is b/100, so
 * "200 * 15%" is 30 and "(20%)" is 0.2.
 */

type Rat = { n: bigint; d: bigint };

export type ArithResult =
  | { ok: true; value: string; exact: boolean; expression: boolean }
  | { ok: false; error: string };

const MAX_LENGTH = 200;
const MAX_DEPTH = 20;
const MAX_INT_DIGITS = 15;
const MAX_FRACTION_DIGITS = 10;
/** 10^15: anything this large is a typing mistake, not a quantity or a price. */
const LIMIT = 10n ** 15n;

class ArithError extends Error {}

const abs = (v: bigint) => (v < 0n ? -v : v);
function gcd(a: bigint, b: bigint): bigint {
  a = abs(a);
  b = abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1n;
}
function rat(n: bigint, d = 1n): Rat {
  if (d === 0n) throw new ArithError("Can't divide by zero.");
  if (d < 0n) [n, d] = [-n, -d];
  const g = gcd(n, d);
  const r = { n: n / g, d: d / g };
  // Bound intermediate results too, so a chain of multiplications can't run away.
  if (abs(r.n) >= LIMIT * r.d) throw new ArithError("That number is too large.");
  if (r.d > 10n ** 40n) throw new ArithError("That calculation is too precise to keep.");
  return r;
}
const add = (a: Rat, b: Rat) => rat(a.n * b.d + b.n * a.d, a.d * b.d);
const sub = (a: Rat, b: Rat) => rat(a.n * b.d - b.n * a.d, a.d * b.d);
const mul = (a: Rat, b: Rat) => rat(a.n * b.n, a.d * b.d);
const div = (a: Rat, b: Rat) => {
  if (b.n === 0n) throw new ArithError("Can't divide by zero.");
  return rat(a.n * b.d, a.d * b.n);
};
const ONE: Rat = { n: 1n, d: 1n };
const HUNDRED: Rat = { n: 100n, d: 1n };

type Token = { kind: "num"; value: Rat } | { kind: "op"; value: string };

function tokenize(text: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < text.length) {
    const c = text[i];
    if (c === " " || c === "\t" || c === "$") {
      i += 1;
      continue;
    }
    if ("+-*/()%".includes(c)) {
      tokens.push({ kind: "op", value: c });
      i += 1;
      continue;
    }
    if (c === "x" || c === "X" || c === "×") {
      tokens.push({ kind: "op", value: "*" });
      i += 1;
      continue;
    }
    if (c === "÷") {
      tokens.push({ kind: "op", value: "/" });
      i += 1;
      continue;
    }
    if (c === "−" || c === "–") {
      tokens.push({ kind: "op", value: "-" });
      i += 1;
      continue;
    }
    if ((c >= "0" && c <= "9") || c === ".") {
      let j = i;
      while (j < text.length && /[0-9.,]/.test(text[j])) j += 1;
      const raw = text.slice(i, j);
      // Thousands separators are accepted only in their usual places (1,250.50).
      if (raw.includes(",") && !/^\d{1,3}(,\d{3})+(\.\d*)?$/.test(raw)) throw new ArithError(`"${raw}" isn't a number.`);
      const plain = raw.replace(/,/g, "");
      if (!/^(\d+\.?\d*|\.\d+)$/.test(plain)) throw new ArithError(`"${raw}" isn't a number.`);
      const [whole, fraction = ""] = plain.split(".");
      if (whole.replace(/^0+/, "").length > MAX_INT_DIGITS) throw new ArithError("That number is too large.");
      if (fraction.length > MAX_FRACTION_DIGITS) throw new ArithError(`Use at most ${MAX_FRACTION_DIGITS} decimals.`);
      tokens.push({ kind: "num", value: rat(BigInt((whole || "0") + fraction), 10n ** BigInt(fraction.length)) });
      i = j;
      continue;
    }
    throw new ArithError(`"${c}" can't be used here. Use numbers, + − × ÷, parentheses and %.`);
  }
  return tokens;
}

function parse(tokens: Token[]): Rat {
  let pos = 0;
  let depth = 0;
  const peek = () => tokens[pos];
  const isOp = (v: string) => peek()?.kind === "op" && peek()!.value === v;

  function primary(): Rat {
    const t = peek();
    if (!t) throw new ArithError("The expression ends too early.");
    if (t.kind === "num") {
      pos += 1;
      return t.value;
    }
    if (t.value === "(") {
      pos += 1;
      depth += 1;
      if (depth > MAX_DEPTH) throw new ArithError("Too many parentheses.");
      const v = expr();
      if (!isOp(")")) throw new ArithError("A closing parenthesis is missing.");
      pos += 1;
      depth -= 1;
      return v;
    }
    throw new ArithError(`"${t.value}" is out of place.`);
  }
  // Returns whether the operand was written as a percentage, for "a + b%".
  function postfix(): { v: Rat; percent: boolean } {
    const v = primary();
    if (isOp("%")) {
      pos += 1;
      return { v: div(v, HUNDRED), percent: true };
    }
    return { v, percent: false };
  }
  function unary(): { v: Rat; percent: boolean } {
    if (isOp("-")) {
      pos += 1;
      const u = unary();
      return { v: rat(-u.v.n, u.v.d), percent: u.percent };
    }
    if (isOp("+")) {
      pos += 1;
      return unary();
    }
    return postfix();
  }
  function term(): { v: Rat; percent: boolean } {
    let left = unary();
    while (isOp("*") || isOp("/")) {
      const op = peek()!.value;
      pos += 1;
      const right = unary().v;
      left = { v: op === "*" ? mul(left.v, right) : div(left.v, right), percent: false };
    }
    return left;
  }
  function expr(): Rat {
    let left = term().v;
    while (isOp("+") || isOp("-")) {
      const op = peek()!.value;
      pos += 1;
      const right = term();
      if (right.percent) left = mul(left, op === "+" ? add(ONE, right.v) : sub(ONE, right.v));
      else left = op === "+" ? add(left, right.v) : sub(left, right.v);
    }
    return left;
  }

  const value = expr();
  if (pos < tokens.length) {
    const t = tokens[pos];
    throw new ArithError(t.kind === "op" && t.value === ")" ? "There's an extra closing parenthesis." : "Put an operator between the numbers.");
  }
  return value;
}

/** Round half away from zero to `scale` decimals and format without trailing zeros. */
function format(r: Rat, scale: number): { value: string; exact: boolean } {
  const factor = 10n ** BigInt(scale);
  const scaled = r.n * factor;
  let q = scaled / r.d;
  const rem = scaled % r.d;
  const exact = rem === 0n;
  if (abs(rem) * 2n >= r.d) q += r.n < 0n ? -1n : 1n;
  const negative = q < 0n;
  const digits = abs(q).toString().padStart(scale + 1, "0");
  let whole = digits.slice(0, digits.length - scale);
  let fraction = scale ? digits.slice(-scale) : "";
  fraction = fraction.replace(/0+$/, "");
  whole = whole || "0";
  const text = fraction ? `${whole}.${fraction}` : whole;
  return { value: negative && text !== "0" ? `-${text}` : text, exact };
}

/** Is the text more than a plain number, i.e. worth a "= result" preview? */
export function isExpression(text: string): boolean {
  const t = text.trim().replace(/^[+-]/, "");
  return /[-+*/x×÷%()−–]/i.test(t);
}

/**
 * Evaluate a calculator entry. `scale` is the number of decimals kept (4 by default, enough for
 * quantities and unit prices; the server rounds to its own field precision).
 */
export function evaluate(text: string, { scale = 4, allowNegative = true }: { scale?: number; allowNegative?: boolean } = {}): ArithResult {
  const t = text.trim();
  if (!t) return { ok: false, error: "Enter a number." };
  if (t.length > MAX_LENGTH) return { ok: false, error: "That expression is too long." };
  try {
    const tokens = tokenize(t);
    if (!tokens.length) return { ok: false, error: "Enter a number." };
    const r = parse(tokens);
    if (!allowNegative && r.n < 0n) return { ok: false, error: "The result can't be negative." };
    const { value, exact } = format(r, scale);
    return { ok: true, value, exact, expression: isExpression(t) };
  } catch (e) {
    if (e instanceof ArithError) return { ok: false, error: e.message };
    throw e;
  }
}
